import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
  KnowledgeBaseSummaryViewDto,
  KnowledgeBaseEntryViewDto,
  KnowledgeBaseEntryUpsertDto,
  KnowledgeBaseAiPopulateDto,
  KnowledgeBaseCategoryConfigDto,
  KnowledgeBaseFileViewDto,
  KnowledgeBaseAssetType,
} from '@/types/knowledgeBase'

/** URL-encode the category description for use in route parameters.
 *  The server uses TryFromDescription() which matches the Description attribute
 *  (e.g., "Ideal Customer Profile"), not the enum name. */
export function categoryToUrlParam(category: string): string {
  return encodeURIComponent(category)
}

export const knowledgeBaseApi = {
  // ---------- METADATA ----------

  getCategories: (): Promise<ApiResponse<KnowledgeBaseCategoryConfigDto[]>> =>
    apiClient.get('/api/KnowledgeBase/Categories'),

  // ---------- COMPANY LEVEL ----------

  getCompanySummary: (
    clientCompanyId: string
  ): Promise<ApiResponse<KnowledgeBaseSummaryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase`),

  getCompanyEntry: (
    clientCompanyId: string,
    category: string
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  upsertCompanyEntry: (
    clientCompanyId: string,
    category: string,
    dto: KnowledgeBaseEntryUpsertDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}`, dto),

  uploadCompanyFile: (
    clientCompanyId: string,
    category: string,
    file: File,
    assetType?: KnowledgeBaseAssetType
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> => {
    const formData = new FormData()
    formData.append('file', file)
    const query = assetType ? `?assetType=${assetType}` : ''
    return apiClient.post(
      `/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}/Files${query}`,
      formData
    )
  },

  deleteCompanyFile: (
    clientCompanyId: string,
    category: string,
    fileId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}/Files/${fileId}`),

  // ---------- WORKSPACE LEVEL ----------

  getWorkspaceSummary: (
    clientCompanyId: string,
    workspaceId: string
  ): Promise<ApiResponse<KnowledgeBaseSummaryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase`),

  getWorkspaceEntry: (
    clientCompanyId: string,
    workspaceId: string,
    category: string
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  upsertWorkspaceEntry: (
    clientCompanyId: string,
    workspaceId: string,
    category: string,
    dto: KnowledgeBaseEntryUpsertDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}`, dto),

  deleteWorkspaceOverride: (
    clientCompanyId: string,
    workspaceId: string,
    category: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  uploadWorkspaceFile: (
    clientCompanyId: string,
    workspaceId: string,
    category: string,
    file: File,
    assetType?: KnowledgeBaseAssetType
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> => {
    const formData = new FormData()
    formData.append('file', file)
    const query = assetType ? `?assetType=${assetType}` : ''
    return apiClient.post(
      `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}/Files${query}`,
      formData
    )
  },

  deleteWorkspaceFile: (
    clientCompanyId: string,
    workspaceId: string,
    category: string,
    fileId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}/Files/${fileId}`),

  // ---------- CAMPAIGN LEVEL ----------

  getCampaignSummary: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string
  ): Promise<ApiResponse<KnowledgeBaseSummaryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase`),

  getCampaignEntry: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  upsertCampaignEntry: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string,
    dto: KnowledgeBaseEntryUpsertDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}`, dto),

  deleteCampaignOverride: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  uploadCampaignFile: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string,
    file: File,
    assetType?: KnowledgeBaseAssetType
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> => {
    const formData = new FormData()
    formData.append('file', file)
    const query = assetType ? `?assetType=${assetType}` : ''
    return apiClient.post(
      `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}/Files${query}`,
      formData
    )
  },

  deleteCampaignFile: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string,
    fileId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}/Files/${fileId}`),

  // ---------- AI POPULATION (company level, async via service bus) ----------
  // Returns 202 Accepted immediately; completion arrives via SignalR "KnowledgeBasePopulated"

  aiPopulate: (
    clientCompanyId: string,
    dto: KnowledgeBaseAiPopulateDto
  ): Promise<ApiResponse<{ status: string; message: string }>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/AiPopulate`, dto),

  aiPopulateCategory: (
    clientCompanyId: string,
    category: string,
    dto: KnowledgeBaseAiPopulateDto
  ): Promise<ApiResponse<{ status: string; message: string }>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}/AiPopulate`, dto),
}
